import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { SearchAddon } from 'xterm-addon-search';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Toolbar,
  AppBar,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import apiService from '../services/api';
import 'xterm/css/xterm.css';

const WebTerminal = ({ 
  vmName, 
  onClose, 
  fullscreen = false, 
  onFullscreenToggle,
  apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000'
}) => {
  const terminalRef = useRef(null);
  const terminal = useRef(null);
  const fitAddon = useRef(null);
  const searchAddon = useRef(null);
  const websocket = useRef(null);
  
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [tunnelId, setTunnelId] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cloudInitUserData, setCloudInitUserData] = useState(null);
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);

  // 브라우저 전체화면 API 처리
  const enterFullscreen = useCallback(async () => {
    try {
      const element = document.documentElement;
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }
      setIsFullscreenMode(true);
    } catch (error) {
      // 전체화면 API 실패 시 기존 방식으로 fallback
      if (onFullscreenToggle) {
        onFullscreenToggle();
      }
    }
  }, [onFullscreenToggle]);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      setIsFullscreenMode(false);
    } catch (error) {
      // 전체화면 API 실패 시 기존 방식으로 fallback
      if (onFullscreenToggle) {
        onFullscreenToggle();
      }
    }
  }, [onFullscreenToggle]);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreenMode || document.fullscreenElement) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [isFullscreenMode, enterFullscreen, exitFullscreen]);

  // 전체화면 상태 변경 감지
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreenMode(isCurrentlyFullscreen);
      
      // 터미널 크기 재조정 (다단계 타이밍으로 정확한 크기 조정)
      if (fitAddon.current) {
        // 즉시 한번
        setTimeout(() => {
          fitAddon.current.fit();
        }, 50);
        
        // 조금 후 다시 한번 (레이아웃 완료 후)
        setTimeout(() => {
          fitAddon.current.fit();
        }, 200);
        
        // 마지막으로 한번 더 (애니메이션 완료 후)
        setTimeout(() => {
          fitAddon.current.fit();
        }, 500);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // 터미널 초기화
  const initializeTerminal = useCallback(() => {
    if (terminal.current) {
      terminal.current.dispose();
    }

    terminal.current = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#ffffff',
        cursor: '#ffffff',
        selection: '#3e3e3e',
        // 256 컬러 지원을 위한 기본 색상 설정
        black: '#000000',
        red: '#cd0000',
        green: '#00cd00',
        yellow: '#cdcd00',
        blue: '#0000ee',
        magenta: '#cd00cd',
        cyan: '#00cdcd',
        white: '#e5e5e5',
        brightBlack: '#7f7f7f',
        brightRed: '#ff0000',
        brightGreen: '#00ff00',
        brightYellow: '#ffff00',
        brightBlue: '#5c5cff',
        brightMagenta: '#ff00ff',
        brightCyan: '#00ffff',
        brightWhite: '#ffffff'
      },
      allowProposedApi: true,
      convertEol: true,
      scrollback: 1000,
      macOptionIsMeta: true,
      windowsMode: false,
      disableStdin: false,
      rightClickSelectsWord: false,
      fastScrollModifier: 'alt',
      allowTransparency: false
    });

    // 애드온 추가
    fitAddon.current = new FitAddon();
    searchAddon.current = new SearchAddon();
    
    terminal.current.loadAddon(fitAddon.current);
    terminal.current.loadAddon(new WebLinksAddon());
    terminal.current.loadAddon(searchAddon.current);

    // DOM에 터미널 연결
    if (terminalRef.current) {
      terminal.current.open(terminalRef.current);
      fitAddon.current.fit();
    }

    // 터미널 입력 처리
    terminal.current.onData((data) => {
      if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
        const message = {
          type: 'input',
          data: data
        };
        websocket.current.send(JSON.stringify(message));
      }
    });

    // 터미널 크기 변경 처리
    terminal.current.onResize(({ cols, rows }) => {
      if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
        websocket.current.send(JSON.stringify({
          type: 'resize',
          cols: cols,
          rows: rows
        }));
      }
    });

    return () => {
      if (terminal.current) {
        terminal.current.dispose();
      }
    };
  }, []);

  // SSH 터널 연결
  const connectTunnel = useCallback(async () => {
    setIsConnecting(true);
    setConnectionError(null);

    try {
      const response = await apiService.connectVMTerminal(vmName);
      setTunnelId(response.data.tunnel_id);
      
      // Cloud-init 사용자 정보 저장
      if (response.data.cloud_init_user_data) {
        setCloudInitUserData(response.data.cloud_init_user_data);
        // Cloud-init 사용자 정보 수신
      }
      
      return response.data.tunnel_id;
    } catch (error) {
      const errorData = error.response?.data?.detail || error.message || '터미널 연결 실패';
      let errorMessage = '';
      
      // 상세 진단 정보가 있는 경우
      if (typeof errorData === 'object' && errorData.diagnostic) {
        const diagnostic = errorData.diagnostic;
        const suggestions = errorData.suggestions || [];
        
                 errorMessage = `${errorData.message}\n\n진단 결과:\n`;
         errorMessage += `- VM 실행 상태: ${diagnostic.vm_running ? '실행 중' : '정지됨'}\n`;
         errorMessage += `- IP 주소: ${diagnostic.ip_address || '없음'}\n`;
         errorMessage += `- 네트워크 연결: ${diagnostic.network_reachable ? '정상' : '실패'}\n`;
         
         if (diagnostic.ping_success) {
           errorMessage += `- 핑 응답: 성공 (${diagnostic.ping_time ? diagnostic.ping_time + 'ms' : '시간 미측정'})\n`;
         } else {
           errorMessage += `- 핑 응답: 실패\n`;
         }
         
         errorMessage += `- 라우팅: ${diagnostic.route_available ? '정상' : '문제'}\n`;
         errorMessage += `- SSH 포트: ${diagnostic.ssh_port_open ? '열림' : '닫힘'}\n`;
         errorMessage += `- SSH 서비스: ${diagnostic.ssh_service_responsive ? '응답함' : '응답 없음'}\n`;
        
        if (suggestions.length > 0) {
          errorMessage += `\n해결 방법:\n${suggestions.map(s => `• ${s}`).join('\n')}`;
        }
      } else {
        errorMessage = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
        
        // SSH 키 인증 실패 메시지 표시
        if (errorMessage.includes('인증') || errorMessage.includes('authentication')) {
          setConnectionError('SSH 키 인증에 실패했습니다. VM에 호스트 SSH 키가 올바르게 설정되었는지 확인해주세요.');
          throw new Error(errorMessage);
        }
      }
      
      setConnectionError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  }, [vmName]);

  // WebSocket 연결
  const connectWebSocket = useCallback((tunnelId) => {
    // WebSocket URL은 현재 호스트를 기준으로 계산 (apiUrl 사용하지 않음)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const publicUrl = process.env.PUBLIC_URL || '';
    const wsUrl = `${protocol}//${host}${publicUrl}/ws/terminal/${tunnelId}`;
    
    // WebSocket URL 결정됨
    websocket.current = new WebSocket(wsUrl);

    websocket.current.onopen = () => {
      // 연결 성공 - virsh console은 인증 없이 바로 진행
      // WebSocket 연결 성공
      setIsConnected(true);
      setConnectionError(null);
      terminal.current?.focus();
    };

    websocket.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') {
          // virsh console 연결 성공
          // Virsh console 연결 성공
        } else if (data.type === 'data') {
          // 데이터를 터미널에 직접 쓰기 (xterm.js의 convertEol이 처리함)
          terminal.current?.write(data.data);
        }
      } catch (error) {
        // JSON이 아닌 경우 직접 출력 (virsh console 원시 출력)
        terminal.current?.write(event.data);
      }
    };

    websocket.current.onclose = (event) => {
      setIsConnected(false);
      if (event.code !== 1000) { // 정상 종료가 아닌 경우
        let errorMessage = `연결이 종료되었습니다. (Code: ${event.code})`;
        if (event.code === 4001) {
          errorMessage = '인증 토큰을 받지 못했습니다.';
        } else if (event.code === 4002) {
          errorMessage = '잘못된 인증 메시지 형식입니다.';
        } else if (event.code === 4003) {
          errorMessage = '유효하지 않은 토큰입니다.';
        } else if (event.code === 4004) {
          errorMessage = '비활성화된 사용자입니다.';
        } else if (event.code === 4007) {
          errorMessage = '터널에 대한 접근 권한이 없습니다.';
        }
        setConnectionError(errorMessage);
      }
    };

    websocket.current.onerror = () => {
      setConnectionError('WebSocket 연결 오류가 발생했습니다.');
    };
  }, [apiUrl]);

  // 연결 해제
  const disconnect = useCallback(async () => {
    if (websocket.current) {
      websocket.current.close();
      websocket.current = null;
    }

    if (tunnelId) {
      try {
        await apiService.disconnectTerminal(tunnelId);
      } catch (error) {
        // 터널 해제 오류 무시
      }
      setTunnelId(null);
    }

    setIsConnected(false);
    setConnectionError(null);
  }, [tunnelId]);

  // 재연결
  const reconnect = useCallback(async () => {
    await disconnect();
    try {
      const newTunnelId = await connectTunnel();
      connectWebSocket(newTunnelId);
    } catch (error) {
      // 재연결 실패는 UI에서 처리함
    }
  }, [disconnect, connectTunnel, connectWebSocket]);


  // SSH 키 정보 명령 실행
  const runSSHKeysCommand = useCallback(() => {
    if (terminal.current && websocket.current && websocket.current.readyState === WebSocket.OPEN) {
      const command = 'ssh-keys\n';
      websocket.current.send(JSON.stringify({
        type: 'input',
        data: command
      }));
      terminal.current.focus();
    } else {
      setConnectionError('터미널이 연결되지 않았습니다. 먼저 연결해주세요.');
    }
  }, []);

  // Cloud-init SSH 연결 정보 표시
  const showSSHConnectionInfo = useCallback(() => {
    if (cloudInitUserData) {
      const { username, hostname, ssh_port } = cloudInitUserData;
      const sshCommand = `ssh ${username}@${hostname || vmName}${ssh_port !== 22 ? ` -p ${ssh_port}` : ''}`;
      
      if (terminal.current && websocket.current && websocket.current.readyState === WebSocket.OPEN) {
        // 터미널에 SSH 연결 정보 출력
        const infoMessage = `\r\n=== Cloud-init SSH 연결 정보 ===\r\n`;
        const userInfo = `사용자명: ${username}\r\n`;
        const hostInfo = `호스트명: ${hostname || vmName}\r\n`;
        const portInfo = `SSH 포트: ${ssh_port}\r\n`;
        const commandInfo = `연결 명령: ${sshCommand}\r\n`;
        const separator = `================================\r\n`;
        
        terminal.current.write(infoMessage + userInfo + hostInfo + portInfo + commandInfo + separator);
        terminal.current.focus();
      }
    } else {
      if (terminal.current) {
        terminal.current.write('\r\nCloud-init 사용자 정보를 찾을 수 없습니다.\r\n');
        terminal.current.focus();
      }
    }
  }, [cloudInitUserData, vmName]);

  // 검색 기능
  const handleSearch = (direction = 'next') => {
    if (searchAddon.current && searchTerm) {
      if (direction === 'next') {
        searchAddon.current.findNext(searchTerm);
      } else {
        searchAddon.current.findPrevious(searchTerm);
      }
    }
  };

  // 터미널 크기 조정
  const resizeTerminal = useCallback(() => {
    if (fitAddon.current && terminal.current) {
      try {
        fitAddon.current.fit();
        
        // WebSocket으로 터미널 크기 변경 정보 전송
        if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
          const cols = terminal.current.cols;
          const rows = terminal.current.rows;
          const resizeMessage = {
            type: 'resize',
            cols: cols,
            rows: rows
          };
          websocket.current.send(JSON.stringify(resizeMessage));
        }
      } catch (error) {
        // 터미널 크기 조정 실패는 무시
      }
    }
  }, []);

  // 컴포넌트 마운트/언마운트 처리
  useEffect(() => {
    const cleanup = initializeTerminal();
    
    // 자동 연결
    const autoConnect = async () => {
      try {
        const newTunnelId = await connectTunnel();
        connectWebSocket(newTunnelId);
      } catch (error) {
        // 자동 연결 실패는 UI에서 처리함
      }
    };

    autoConnect();

    // 윈도우 크기 변경 시 터미널 크기 조정
    const handleResize = () => resizeTerminal();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      disconnect();
      cleanup();
    };
  }, [initializeTerminal, connectTunnel, connectWebSocket, disconnect, resizeTerminal]);

  // 전체화면 모드 변경 시 터미널 크기 조정
  useEffect(() => {
    const timer = setTimeout(() => {
      resizeTerminal();
    }, 100);
    return () => clearTimeout(timer);
  }, [fullscreen, isFullscreenMode, resizeTerminal]);

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        height: isFullscreenMode ? '100vh' : (fullscreen ? '100vh' : 500), 
        width: isFullscreenMode ? '100vw' : 'auto',
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#1e1e1e',
        position: isFullscreenMode ? 'fixed' : 'relative',
        top: isFullscreenMode ? 0 : 'auto',
        left: isFullscreenMode ? 0 : 'auto',
        zIndex: isFullscreenMode ? 9999 : 'auto'
      }}
    >
      {/* 터미널 헤더 */}
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            터미널: {vmName}
            {cloudInitUserData && (
              <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                ({cloudInitUserData.username}@{cloudInitUserData.hostname || vmName}:{cloudInitUserData.ssh_port})
              </Typography>
            )}
          </Typography>
          
          {/* 연결 상태 표시 */}
          <Chip
            label={isConnected ? '연결됨' : (isConnecting ? '연결 중...' : '연결 해제')}
            color={isConnected ? 'success' : (isConnecting ? 'warning' : 'error')}
            size="small"
            sx={{ mr: 1 }}
          />

          {/* 터미널 제어 버튼들 */}
          <IconButton 
            onClick={() => setShowSearch(!showSearch)} 
            size="small"
            title="검색"
          >
            <SearchIcon />
          </IconButton>
          
          <IconButton 
            onClick={reconnect} 
            size="small"
            title="재연결"
            disabled={isConnecting}
          >
            {isConnecting ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>

          <IconButton 
            onClick={showSSHConnectionInfo} 
            size="small"
            title={cloudInitUserData ? `SSH 연결 정보 (${cloudInitUserData.username}@${cloudInitUserData.hostname || vmName})` : "Cloud-init SSH 정보"}
            color={cloudInitUserData ? "success" : "default"}
            disabled={!isConnected}
          >
            <SettingsIcon />
          </IconButton>

          <IconButton 
            onClick={toggleFullscreen} 
            size="small"
            title={isFullscreenMode ? "전체화면 해제" : "전체화면"}
          >
            {isFullscreenMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>

          <IconButton onClick={onClose} size="small" title="닫기">
            <CloseIcon />
          </IconButton>
        </Toolbar>
        
        {/* 검색 바 */}
        {showSearch && (
          <Box sx={{ p: 1, bgcolor: 'grey.100' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="검색어 입력..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch('next');
                }
              }}
              InputProps={{
                endAdornment: (
                  <Box>
                    <Button size="small" onClick={() => handleSearch('previous')}>
                      이전
                    </Button>
                    <Button size="small" onClick={() => handleSearch('next')}>
                      다음
                    </Button>
                  </Box>
                )
              }}
            />
          </Box>
        )}
      </AppBar>

      {/* 연결 오류 메시지 */}
      {connectionError && (
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={reconnect}>
              재시도
            </Button>
          }
        >
          {connectionError}
        </Alert>
      )}

      {/* 터미널 영역 */}
      <Box 
        ref={terminalRef}
        sx={{ 
          flexGrow: 1, 
          bgcolor: '#1e1e1e',
          '& .xterm': {
            height: '100% !important',
            width: '100% !important'
          },
          '& .xterm-viewport': {
            overflow: 'hidden !important'
          }
        }}
      />

      {/* 연결 중 오버레이 */}
      {isConnecting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>터미널에 연결 중...</Typography>
          </Box>
        </Box>
      )}


    </Paper>
  );
};

export default WebTerminal; 